import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Plus, Minus, X, Send, Loader2, Lock, WifiOff, MessageCircle, Trash2, Clock, AlertTriangle, LogOut, UserCircle2, Eye, HelpingHand } from "lucide-react";
import { toast } from "sonner";
import { api, rp, API, setActiveTokenKey, clearToken, getToken, pickStoreTokenKey, storeLogoUrl } from "@/api";
import { InstallBanner } from "@/components/InstallBanner";
import { PinLogin } from "@/components/PinLogin";
import { MyOrders } from "@/components/MyOrders";
import { DepositMitraPanel } from "@/components/DepositPanel";
import { MitraMikrotikPanel, AdminEntryMikrotikPanel } from "@/components/MikrotikPanel";
import { cacheStore, getCachedStore, getPending, savePending, newRef, isNetworkError, flushPending } from "@/offline";

function PendingOrders({ items, onRemove }) {
  if (items.length === 0) return null;
  return (
    <div className="pending-box" data-testid="pending-box">
      <h4>Pesanan Tertunda ({items.length})</h4>
      {items.map((p) => (
        <div className="pending-row" key={p.ref} data-testid={`pending-${p.ref}`}>
          <div>
            <strong>{p.buyer_name || "Tanpa nama"}</strong> · {rp(p.total)}
            <div className="muted-sm" data-testid={`pending-state-${p.ref}`}>
              {p.state === "pending" && <><Clock size={11} /> Menunggu sinyal — akan terkirim otomatis</>}
              {p.state === "sent" && <>Terkirim ke server · {new Date(p.sent_at).toLocaleTimeString("id-ID")}</>}
              {p.state === "failed" && <><AlertTriangle size={11} /> Gagal: {p.error}</>}
            </div>
          </div>
          <div className="pending-actions">
            {p.state === "sent" && (
              <a className="wa-btn" href={p.wa_url} target="_blank" rel="noreferrer" data-testid={`pending-wa-${p.ref}`}><MessageCircle size={13} /> Kirim ke WhatsApp</a>
            )}
            <button className="icon-btn danger" onClick={() => onRemove(p.ref)} title="Hapus" data-testid={`pending-remove-${p.ref}`}><Trash2 size={15} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

const QUICK_ADDS = [5, 10, 15, 20];

// Layar terkunci: trial/langganan berakhir. Data toko TIDAK dihapus — hanya akses dibatasi.
function LockedStore({ store, access }) {
  const isTrial = access.reason === "trial_ended";
  const endRaw = isTrial ? access.trial_due_at : access.due_date;
  const endText = endRaw ? new Date(endRaw).toLocaleString("id-ID") : "-";
  const wa = (store?.whatsapp_number || "").replace(/\D/g, "");
  return (
    <div className="app-shell">
      <div className="container" style={{ maxWidth: 520, margin: "60px auto", textAlign: "center" }}>
        <div data-testid="locked-store-screen" style={{ background: "#fff", border: "1px solid #fecaca", borderRadius: 14, padding: "28px 20px" }}>
          <Lock size={40} color="#b91c1c" style={{ margin: "0 auto 10px" }} />
          <h1 style={{ fontSize: 22, color: "#b91c1c", margin: "0 0 6px" }}>AKUN TERKUNCI</h1>
          {store.logo_path
            ? <img src={storeLogoUrl(store)} alt={`Logo ${store.store_name}`} className="store-logo" style={{ margin: "0 auto 10px" }} data-testid="locked-store-logo" />
            : <div className="store-logo store-logo-default" style={{ margin: "0 auto 10px" }} data-testid="locked-store-logo-default">{(store.store_name || "?").trim().charAt(0).toUpperCase()}</div>}
          <p style={{ fontWeight: 700, margin: "0 0 10px" }}>{store.store_name}</p>
          <p className="muted-sm" style={{ margin: "0 0 4px" }}>
            {access.state === "locked" ? "Toko sedang dinonaktifkan oleh administrator." : isTrial ? "Masa Trial 5 hari telah berakhir." : "Masa langganan telah berakhir."}
          </p>
          <p className="muted-sm" style={{ margin: "0 0 12px" }}>
            {isTrial ? "Trial berakhir" : "Langganan berakhir"}: <strong>{endText}</strong>
            {" · "}Status: <strong>{access.state === "locked" ? "Terkunci" : "Expired"}</strong>
          </p>
          <p className="muted-sm">Silakan hubungi administrator untuk mengaktifkan kembali / berlangganan.</p>
          {wa && <a className="wa-btn" style={{ marginTop: 10, display: "inline-flex" }} href={`https://wa.me/${wa}?text=${encodeURIComponent(`Halo, saya pemilik toko ${store.store_name}. Mohon info perpanjangan layanan.`)}`} target="_blank" rel="noreferrer" data-testid="locked-contact-admin"><MessageCircle size={14} /> Hubungi Administrator</a>}
        </div>
      </div>
    </div>
  );
}

function VoucherRow({ v, qty, onChange, price, priceLabel }) {
  const [text, setText] = useState(String(qty));
  useEffect(() => { setText(String(qty)); }, [qty]);

  const commit = (raw) => {
    const n = Math.max(0, Math.min(9999, parseInt(raw, 10) || 0));
    onChange(n);
    setText(String(n));
  };

  return (
    <div className="voucher-card" style={{ backgroundColor: v.color }} data-testid={`voucher-card-${v.id}`}>
      <div className="voucher-top">
        <div className="voucher-info">
          <div className="voucher-name" data-testid={`voucher-name-${v.id}`}>{v.name}</div>
          <div className="voucher-bottom">
            <div className="price-row">
              <span className="voucher-price" data-testid={`voucher-buy-price-${v.id}`}>{priceLabel || "Beli"} {rp(price ?? v.harga_beli)}</span>
              <span className="voucher-price buy" data-testid={`voucher-price-${v.id}`}>Jual {rp(v.harga_jual)}</span>
            </div>
            <div className="stepper">
              <button className="step-btn" onClick={() => onChange(Math.max(0, qty - 1))} data-testid={`voucher-dec-${v.id}`} aria-label="kurangi">
                <Minus size={18} strokeWidth={3} />
              </button>
              <input
                className="qty-input"
                type="number"
                min="0"
                inputMode="numeric"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onBlur={(e) => commit(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") commit(e.target.value); }}
                data-testid={`voucher-qty-${v.id}`}
                aria-label="jumlah"
              />
              <button className="step-btn" onClick={() => onChange(qty + 1)} data-testid={`voucher-inc-${v.id}`} aria-label="tambah">
                <Plus size={18} strokeWidth={3} />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="quick-adds">
        {QUICK_ADDS.map((n) => (
          <button key={n} className="quick-btn" onClick={() => onChange(qty + n)} data-testid={`voucher-add${n}-${v.id}`}>+{n}</button>
        ))}
        {qty > 0 && (
          <button className="quick-btn reset" onClick={() => onChange(0)} data-testid={`voucher-reset-${v.id}`}>Reset</button>
        )}
      </div>
    </div>
  );
}

export default function StorePage() {
  const params = useParams();
  const slug = (params.slug || "").toLowerCase();
  useMemo(() => setActiveTokenKey(pickStoreTokenKey(slug)), [slug]);
  const [store, setStore] = useState(null);
  const [locked, setLocked] = useState(true);
  const [viewer, setViewer] = useState(null);
  const [resellers, setResellers] = useState([]);
  const [helpedId, setHelpedId] = useState("");
  const [note, setNote] = useState("");
  const [ordersKey, setOrdersKey] = useState(0);
  const [vouchers, setVouchers] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [qtys, setQtys] = useState({});
  const [extras, setExtras] = useState([{ name: "", price: "" }]);
  const [sending, setSending] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [fromCache, setFromCache] = useState(false);
  const [pending, setPending] = useState(getPending());
  const [tunggakan, setTunggakan] = useState(null);
  const [access, setAccess] = useState(null);   // status akses trial/langganan dari server
  const [priceMap, setPriceMap] = useState({}); // {reseller_id: {voucher_id: harga}} — harga mengikuti skema mitra tujuan

  // per-store PWA manifest so "Add to Home Screen" opens this store
  useEffect(() => {
    if (!slug) return;
    let link = document.querySelector('link[rel="manifest"]');
    if (!link) { link = document.createElement("link"); link.rel = "manifest"; document.head.appendChild(link); }
    link.href = `${API}/public/stores/${slug}/manifest.json`;
    return () => { link.href = "/manifest.json"; };
  }, [slug]);

  const load = (keyOverride, retry = 0) => {
    const key = keyOverride || pickStoreTokenKey(slug);
    setActiveTokenKey(key);
    return api.get(`/public/stores/${slug}`)
    .then((r) => {
      setStore(r.data.store); setVouchers(r.data.vouchers || []); setLocked(r.data.locked); setViewer(r.data.viewer); setResellers(r.data.resellers || []);
      setTunggakan(r.data.rekap_tunggakan || null);
      setPriceMap(r.data.price_map || {});
      setAccess({ state: r.data.access_state, reason: r.data.access_reason, trial_due_at: r.data.trial_due_at, due_date: r.data.due_date, billing_plan: r.data.billing_plan, server_time: r.data.server_time });
      setNotFound(false); setFromCache(false);
      if (r.data.locked && getToken(key)) {
        clearToken(key);
        if (pickStoreTokenKey(slug) !== key) return load();
      }
      if (!r.data.locked) cacheStore(slug, r.data);
      if (r.data.store?.store_name) document.title = r.data.store.store_name;
    })
    .catch((e) => {
      const cached = isNetworkError(e) ? getCachedStore(slug) : null;
      if (cached) { setStore(cached.store); setVouchers(cached.vouchers); setViewer(cached.viewer); setLocked(false); setFromCache(true); setNotFound(false); }
      else if (e?.response?.status === 404) setNotFound(true);
      else if (retry < 4) { setNotFound(false); return new Promise((res) => setTimeout(res, 2000 * (retry + 1))).then(() => load(keyOverride, retry + 1)); } // server belum bangun / gagal sementara: coba ulang, jangan tampilkan "Toko tidak ditemukan"
      else setNotFound(true);
    });
  };

  useEffect(() => { if (slug) load(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const logoutReseller = () => { clearToken(`reseller:${slug}`); setViewer(null); setVouchers([]); setQtys({}); load(); };

  const syncPending = async () => {
    if (!slug) return;
    const before = getPending().filter((p) => p.slug === slug && p.state === "pending").length;
    const list = await flushPending(slug);
    setPending(list);
    const after = list.filter((p) => p.slug === slug && p.state === "pending").length;
    if (before > 0 && after < before) setOrdersKey((k) => k + 1);
    if (before > 0 && after < before) toast.success(`${before - after} pesanan tertunda berhasil terkirim. Klik "Kirim ke WhatsApp".`);
  };

  useEffect(() => {
    const up = () => { setOnline(true); syncPending(); };
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    if (navigator.onLine) syncPending();
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const removePending = (ref) => { const list = getPending().filter((p) => p.ref !== ref); savePending(list); setPending(list); };

  // Harga mengikuti SKEMA mitra tujuan (bukan role pencatat): saat Agen membantu mitra Konsinyasi,
  // harga yang tampil/dipakai adalah Harga Konsinyasi milik mitra itu.
  const helpedObj = resellers.find((r) => r.id === helpedId) || null;
  const priceFor = (v) => (helpedObj ? (priceMap?.[helpedObj.id]?.[v.id] ?? v.harga_beli) : v.harga_beli);
  const SCHEME_LABEL = { agen: "Harga Agen", konsinyasi: "Harga Konsinyasi", reseller: "Harga Reseller", semi_reseller: "Harga Semi Reseller", normal: "Harga Reseller" };
  const priceLabel = helpedObj ? (SCHEME_LABEL[helpedObj.partner_type] || "Harga Beli") : "Beli";

  const total = useMemo(() => {
    let t = 0;
    vouchers.forEach((v) => { t += (qtys[v.id] || 0) * priceFor(v); });
    extras.forEach((e) => { if (e.name.trim()) t += Number(e.price) || 0; });
    return t;
  }, [vouchers, qtys, extras, helpedId, priceMap, resellers]); // eslint-disable-line react-hooks/exhaustive-deps

  const setQty = (id, val) => setQtys((q) => ({ ...q, [id]: val }));
  const setExtra = (i, key, val) => setExtras((e) => e.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));
  const addExtra = () => setExtras((e) => [...e, { name: "", price: "" }]);
  const removeExtra = (i) => setExtras((e) => (e.length === 1 ? [{ name: "", price: "" }] : e.filter((_, idx) => idx !== i)));

  // Rekap tunggakan: mengikuti reseller tujuan (yang dibantu), bukan akun pratinjau/pembantu
  const activeResellerId = helpedId || viewer?.id;
  const myTunggakan = tunggakan?.per_reseller?.find((x) => x.reseller_id === activeResellerId)?.total || 0;
  const adaTunggakan = (tunggakan?.total || 0) > 0;
  const can = (k) => viewer?.kind !== "reseller" || viewer?.panels?.[k] !== false;

  const submit = async () => {
    const chosenVouchers = vouchers.filter((v) => (qtys[v.id] || 0) > 0)
      .map((v) => ({ voucher_id: v.id, name: v.name, qty: qtys[v.id], price: priceFor(v), sell_price: v.harga_jual }));
    const chosenExtras = extras.filter((e) => e.name.trim()).map((e) => ({ name: e.name, qty: 1, price: Number(e.price) || 0 }));
    if (chosenVouchers.length === 0 && chosenExtras.length === 0) return;

    const isAdmin = viewer?.kind === "admin";
    const helped = resellers.find((r) => r.id === helpedId) || null;
    if (isAdmin && !helped) { toast.error("Pilih nama reseller yang dibantu terlebih dahulu"); return; }
    // tunggakan & pesanan tercatat ke reseller tujuan (dropdown bawah); pembantu hanya tercatat sebagai assisted_by
    const helpingOther = !!helped && helped.id !== viewer?.id;
    const buyerName = helpingOther ? helped.name : (viewer?.name || "");
    let msg = `*${store.store_name}*\n${store.subtitle}\n\n`;
    if (buyerName) msg += `Reseller/Jalur: ${buyerName}${isAdmin ? ` (dibantu admin ${viewer.name})` : helpingOther ? ` (dibantu reseller ${viewer.name})` : ""}\n`;
    if (note.trim()) msg += `Catatan: ${note}\n`;
    msg += `\n*Pesanan:*\n`;
    chosenVouchers.forEach((v) => { msg += `- ${v.name} x${v.qty} @${rp(v.price)} = ${rp(v.qty * v.price)}\n`; });
    chosenExtras.forEach((e) => { msg += `- ${e.name} = ${rp(e.price)}\n`; });
    msg += `\n*Total: ${rp(total)}*`;
    const waNum = (store.whatsapp_number || "").replace(/\D/g, "");
    const wa = waNum ? `https://wa.me/${waNum}?text=${encodeURIComponent(msg)}` : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    const payload = { buyer_name: buyerName, buyer_note: note, vouchers: chosenVouchers, extras: chosenExtras, total, client_ref: newRef(), reseller_id: helpingOther ? helped.id : undefined };

    const queue = () => {
      const list = [...getPending(), { ref: payload.client_ref, slug, payload, wa_url: wa, buyer_name: buyerName, total, state: "pending", created_at: Date.now() }];
      savePending(list); setPending(list);
      setQtys({}); setExtras([{ name: "", price: "" }]); setNote("");
      toast.warning("Tidak ada sinyal. Pesanan disimpan dan akan terkirim otomatis saat online.", { duration: 6000 });
    };

    if (!navigator.onLine) { queue(); return; }
    setSending(true);
    try {
      await api.post(`/public/stores/${slug}/orders`, payload);
      window.open(wa, "_blank");
      setQtys({}); setExtras([{ name: "", price: "" }]); setNote("");
      setOrdersKey((k) => k + 1);
      toast.success("Pesanan tersimpan dan dibuka di WhatsApp");
    } catch (e) {
      if (isNetworkError(e)) queue();
      else if (e?.response?.status === 401) { toast.error("Sesi berakhir, masukkan PIN lagi"); logoutReseller(); }
      else toast.error("Pesanan ditolak server. Coba lagi.");
    } finally {
      setSending(false);
    }
  };

  if (notFound) {
    return (
      <div className="app-shell center-note" data-testid="store-not-found">
        <div>
          <h1 className="app-title">Toko tidak ditemukan</h1>
          <p className="app-subtitle">Periksa kembali link yang Anda buka.</p>
          <Link to="/" className="link-btn">Ke halaman utama</Link>
        </div>
      </div>
    );
  }
  if (!store) return <div className="app-shell center-note">Memuat...</div>;
  // auto-lock: trial/langganan berakhir -> layar terkunci (berlaku juga sebelum login PIN)
  if (access && (access.state === "expired" || access.state === "locked")) return <LockedStore store={store} access={access} />;
  if (locked) return <PinLogin slug={slug} store={store} onSuccess={() => { setActiveTokenKey(`reseller:${slug}`); load(`reseller:${slug}`); }} />;

  return (
    <div className="app-shell">
      <div className="container">
        <header className="app-header">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {store.logo_path
              ? <img src={storeLogoUrl(store)} alt={`Logo ${store.store_name}`} className="store-logo" data-testid="store-logo" />
              : <div className="store-logo store-logo-default" data-testid="store-logo-default">{(store.store_name || "?").trim().charAt(0).toUpperCase()}</div>}
            <div>
              <h1 className="app-title" data-testid="store-name">{store.store_name}</h1>
              <p className="app-subtitle" data-testid="store-subtitle">{store.subtitle}</p>
            </div>
          </div>
          <div className="header-actions">
            {(!online || fromCache) && (
              <span className="offline-badge" data-testid="offline-badge"><WifiOff size={13} /> {online ? "Data tersimpan" : "Offline"}</span>
            )}
            <Link className="gear-btn" to={`/toko/${slug}/admin`} data-testid="open-admin" title="Admin toko">
              <Lock size={20} />
            </Link>
          </div>
        </header>

        <div className={`viewer-bar ${viewer?.kind === "admin" ? "admin" : ""}`} data-testid="viewer-bar">
          <span className="viewer-name" data-testid="viewer-name">
            {viewer?.kind === "admin" ? <><Eye size={15} /> Pratinjau admin: {viewer.name}</> : <><UserCircle2 size={15} /> Reseller: {viewer?.name}</>}
          </span>
          {viewer?.kind === "admin" && (
            <button className="btn-ghost sm" onClick={() => { setActiveTokenKey(`reseller:${slug}`); load(`reseller:${slug}`); }} data-testid="switch-reseller-mode"><UserCircle2 size={13} /> Mode Reseller</button>
          )}
          {viewer?.kind === "reseller" && (
            <button className="btn-ghost sm" onClick={logoutReseller} data-testid="reseller-logout"><LogOut size={13} /> Keluar</button>
          )}
        </div>

        {access?.state === "trial" && access.trial_due_at && access.server_time && (
          (() => {
            const ms = new Date(access.trial_due_at) - new Date(access.server_time);
            const h = Math.max(0, Math.floor(ms / 3600000));
            return (
              <div className="trial-banner" data-testid="trial-banner" style={{ background: "#ede9fe", border: "1px solid #c4b5fd", color: "#6d28d9", borderRadius: 10, padding: "8px 12px", marginBottom: 12, fontSize: 13, fontWeight: 600 }}>
                TRIAL AKTIF — Sisa Trial: {Math.floor(h / 24)} hari {h % 24} jam (berakhir {new Date(access.trial_due_at).toLocaleString("id-ID")})
              </div>
            );
          })()
        )}

        <InstallBanner />
        <PendingOrders items={pending.filter((p) => p.slug === slug)} onRemove={removePending} />

        {can("data_reseller") && <>
        <h2 className="section-heading" id="data-reseller-section" style={{ scrollMarginTop: 12 }}>Data Reseller / Jalur</h2>
        {viewer?.kind === "admin" ? (
          <div className="assist-box" data-testid="assist-box">
            <p className="field-label">Pesanan dibantu admin <strong>{viewer.name}</strong> — pilih reseller yang dipesankan:</p>
            <select className="text-input buyer-input" value={helpedId} onChange={(e) => setHelpedId(e.target.value)} data-testid="assist-reseller-select">
              <option value="">— Pilih reseller —</option>
              {resellers.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            {resellers.length === 0 && <p className="error-text">Belum ada reseller aktif. Tambahkan dulu di panel admin → Reseller & PIN.</p>}
          </div>
        ) : (
          <>
            <input className="text-input buyer-input" value={viewer?.name || ""} readOnly data-testid="buyer-name" />
            {/* Reseller bantu entry reseller lain */}
            <select className="text-input buyer-input" value={helpedId} onChange={(e) => setHelpedId(e.target.value)} data-testid="topup-pilih-reseller-select">
              <option value="">— Pilih reseller (top up untuk diri sendiri) —</option>
              {resellers.map((r) => <option key={r.id} value={r.id}>{r.name}{r.id === viewer?.id ? " (Anda / Pratinjau)" : ""}</option>)}
            </select>
            {helpedId && helpedId !== viewer?.id && (
              <span className="status-pill assisted" style={{ background: "#dbeafe", color: "#1e40af", marginTop: 4, display: "inline-block" }} data-testid="badge-reseller-dibantu">
                <HelpingHand size={11} /> reseller dibantu reseller — untuk {resellers.find((r) => r.id === helpedId)?.name}
              </span>
            )}
          </>
        )}
        </>}
        <input className="text-input buyer-input" placeholder="Catatan (opsional)" value={note}
          onChange={(e) => setNote(e.target.value)} data-testid="buyer-note" />

        {viewer?.kind === "reseller" && online && can("ajukan_deposit") && <DepositMitraPanel slug={slug} />}

        {/* Rekap tagihan per reseller — hanya tampil jika ada tunggakan */}
        {adaTunggakan && can("rekap_tunggakan") && (
          <div className="arrears-box" data-testid="rekap-tunggakan-display" style={{ background: "#fef3c7", border: "1px solid #f59e0b", borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}>
            <h4 style={{ margin: "0 0 6px", fontSize: 12, textTransform: "uppercase", letterSpacing: 1, color: "#92400e" }}>Rekap Tagihan (Tunggakan)</h4>
            {tunggakan.per_reseller.map((t) => (
              <div key={t.reseller_id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#92400e" }}>
                <span style={t.reseller_id === activeResellerId ? { fontWeight: 700 } : {}}>
                  {t.reseller_name}{t.reseller_id === viewer?.id ? " (Anda)" : ""} · {t.count} pesanan
                </span>
                <strong>{rp(t.total)}</strong>
              </div>
            ))}
          </div>
        )}

        {can("pilih_voucher") && <>
        <h2 className="section-heading">Pilih Voucher</h2>
        <div className="voucher-list">
          {vouchers.filter((v) => v.kategori !== "ongkir").map((v) => (
            <VoucherRow key={v.id} v={v} qty={qtys[v.id] || 0} price={priceFor(v)} priceLabel={priceLabel} onChange={(val) => setQty(v.id, val)} />
          ))}
          {vouchers.filter((v) => v.kategori !== "ongkir").length === 0 && <p className="empty-note">Belum ada voucher.</p>}
        </div>

        {vouchers.some((v) => v.kategori === "ongkir") && (
          <>
            <h2 className="section-heading" data-testid="ongkir-heading">Ongkir</h2>
            <div className="voucher-list" data-testid="ongkir-list">
              {vouchers.filter((v) => v.kategori === "ongkir").map((v) => (
                <VoucherRow key={v.id} v={v} qty={qtys[v.id] || 0} price={priceFor(v)} priceLabel={priceLabel} onChange={(val) => setQty(v.id, val)} />
              ))}
            </div>
          </>
        )}
        </>}

        {can("item_lain") && <>
        <h2 className="section-heading">Item Lain-lain</h2>
        <div className="extras-block">
          <p className="field-label muted">Lain-lain (opsional)</p>
          {extras.map((e, i) => (
            <div className="extra-row" key={i} data-testid={`extra-row-${i}`}>
              <input className="text-input extra-name" placeholder="Nama item (mis. Kabel)" value={e.name}
                onChange={(ev) => setExtra(i, "name", ev.target.value)} data-testid={`extra-name-${i}`} />
              <div className="extra-price-wrap">
                <span className="rp-prefix">Rp</span>
                <input className="text-input extra-price" type="number" placeholder="0" value={e.price}
                  onChange={(ev) => setExtra(i, "price", ev.target.value)} data-testid={`extra-price-${i}`} />
              </div>
              <button className="icon-btn danger" onClick={() => removeExtra(i)} data-testid={`extra-remove-${i}`}><X size={18} /></button>
            </div>
          ))}
          <button className="add-item-btn" onClick={addExtra} data-testid="add-extra"><Plus size={16} /> Tambah Item</button>
        </div>
        </>}

        {viewer?.kind === "reseller" && online && can("pesanan_saya") && <MyOrders slug={slug} store={store} refreshKey={ordersKey} />}
        {viewer?.kind === "reseller" && online && <MitraMikrotikPanel slug={slug} />}
        {viewer?.kind === "admin" && online && store?.id && <AdminEntryMikrotikPanel storeId={store.id} />}
      </div>

      <footer className="app-footer">
        <div className="footer-inner container">
          <div>
            <p className="muted-sm" style={{ margin: "0 0 2px", fontSize: 11, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }} data-testid="footer-reseller-summary">
              Untuk: <strong data-testid="footer-reseller-name" style={{ color: helpedObj || viewer?.kind !== "admin" ? "#0f172a" : "#b91c1c" }}>{helpedObj?.name || (viewer?.kind === "admin" ? "Belum dipilih" : viewer?.name)}</strong>
              <button type="button" onClick={() => document.getElementById("data-reseller-section")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                style={{ background: "none", border: 0, padding: 0, color: "#2563eb", fontSize: 11, textDecoration: "underline", cursor: "pointer" }} data-testid="footer-reseller-change">Ubah</button>
            </p>
            {adaTunggakan ? (
              <>
                <p className="total-label">Total Keseluruhan</p>
                <p className="total-value" data-testid="total-keseluruhan-display">{rp(total + myTunggakan)}</p>
                <p className="muted-sm" style={{ color: "#92400e" }}>{rp(total)} + tunggakan {rp(myTunggakan)}</p>
              </>
            ) : (
              <>
                <p className="total-label">{helpedObj ? `Total Bayar (${priceLabel})` : "Total Bayar (Harga Beli)"}</p>
                <p className="total-value" data-testid="total-bayar-display">{rp(total)}</p>
              </>
            )}
          </div>
          <div>
            <button className="submit-btn" onClick={submit} disabled={sending || total === 0 || (viewer?.kind === "admin" && !helpedId)} data-testid="submit-order">
              {sending ? <Loader2 className="spin" size={18} /> : online ? <Send size={18} /> : <WifiOff size={18} />} {online ? "Kirim Pesanan" : "Simpan (Offline)"}
            </button>
            {!sending && (total === 0 || (viewer?.kind === "admin" && !helpedId)) && (
              <p className="muted-sm" style={{ margin: "4px 0 0", fontSize: 11 }} data-testid="submit-hint">
                {viewer?.kind === "admin" && !helpedId ? "Pilih reseller yang dibantu dulu agar tombol aktif" : "Pilih voucher / item dulu agar tombol aktif"}
              </p>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}
